import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  BackgroundVariant,
  useReactFlow,
  Panel,
  applyNodeChanges,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import IdeaNode from './IdeaNode';
import { getNodeConfig } from './nodeConfig';
import NoveltyFilterBar from './NoveltyFilterBar';

const nodeTypes = { ideaNode: IdeaNode };
const proOptions = { hideAttribution: true };

// Bridge: exposes useReactFlow() instance to parent via callback ref
function ReactFlowBridge({ onReady }) {
  const instance = useReactFlow();
  useEffect(() => {
    if (onReady) onReady(instance);
  }, [instance, onReady]);
  return null;
}

// Auto fit-view after generation completes
function AutoFitView({ isGenerating, nodeCount }) {
  const { fitView } = useReactFlow();
  const prevGenerating = useRef(isGenerating);
  useEffect(() => {
    if (prevGenerating.current && !isGenerating && nodeCount > 0) {
      const timer = setTimeout(() => {
        fitView({ padding: 0.15, duration: 500, maxZoom: 1.2 });
      }, 300);
      return () => clearTimeout(timer);
    }
    prevGenerating.current = isGenerating;
  }, [isGenerating, nodeCount, fitView]);
  return null;
}

// Breadcrumb rail: shows ancestor path of selected node
function BreadcrumbRail({ selectedNode, nodes, onNodeClick }) {
  const { setCenter, getZoom } = useReactFlow();

  if (!selectedNode) return null;

  // Build ancestor chain by walking up parentIds
  const nodeMap = {};
  nodes.forEach(n => { nodeMap[n.id] = n; });

  const chain = [];
  const visited = new Set();
  let current = nodeMap[selectedNode.id];
  while (current) {
    if (visited.has(current.id)) break;
    visited.add(current.id);
    chain.unshift(current);
    const parentIds = current.data?.parentIds || (current.data?.parentId ? [current.data.parentId] : []);
    current = parentIds[0] ? nodeMap[parentIds[0]] : null;
  }

  if (chain.length <= 1) return null; // No ancestors to show

  const handleCrumbClick = (node) => {
    onNodeClick(node);
    if (node.position) {
      const zoom = getZoom();
      setCenter(
        node.position.x + 110,
        node.position.y + 34,
        { zoom: Math.max(zoom, 0.8), duration: 400 }
      );
    }
  };

  return (
    <Panel position="top-center" style={{ margin: '8px 0 0 0' }}>
      <div className="breadcrumb-rail">
        {chain.map((node, i) => {
          const config = getNodeConfig(node.data?.type);
          const isLast = i === chain.length - 1;
          return (
            <React.Fragment key={node.id}>
              {i > 0 && <span className="breadcrumb-rail-sep">→</span>}
              <button
                className={`breadcrumb-rail-crumb ${isLast ? 'breadcrumb-rail-active' : ''}`}
                onClick={() => handleCrumbClick(node)}
                title={node.data?.label}
              >
                <span style={{ color: config.color, fontSize: 10 }}>{config.icon}</span>
                <span className="breadcrumb-rail-label">
                  {(node.data?.label || '').slice(0, 25)}{(node.data?.label || '').length > 25 ? '…' : ''}
                </span>
              </button>
            </React.Fragment>
          );
        })}
      </div>
    </Panel>
  );
}

// Toolbar inside ReactFlow so useReactFlow() is available
function CanvasToolbar({ searchQuery = '', onSearchChange, nodeCount, onCollapseAll, onExpandAll, hasCollapsed, drillStack, onExitDrill, onJumpToBreadcrumb }) {
  const { fitView } = useReactFlow();
  const handleFit = useCallback(() => {
    fitView({ padding: 0.2, duration: 400 });
  }, [fitView]);

  return (
    <Panel position="top-left" style={{ margin: 12, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'stretch' }}>
      {/* Drill breadcrumb — inside the Panel so it's always visible */}
      {drillStack && drillStack.length > 0 && (
        <div className="drill-breadcrumb-bar">
          <button className="drill-back-btn" onClick={onExitDrill} title="Back to full tree">
            ← ROOT
          </button>
          {drillStack.map((entry, i) => (
            <React.Fragment key={entry.nodeId}>
              <span className="drill-crumb-sep">›</span>
              <button
                className="drill-crumb-label"
                onClick={() => onJumpToBreadcrumb(i)}
                style={{ fontWeight: i === drillStack.length - 1 ? 700 : 400 }}
                title={entry.nodeLabel}
              >
                {entry.nodeLabel}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
      <div className="canvas-toolbar">
        <button
          type="button"
          className="canvas-toolbar-fit"
          onClick={handleFit}
          title="Fit entire tree in view"
        >
          ⊡ Fit view
        </button>
        {hasCollapsed ? (
          <button
            type="button"
            className="canvas-toolbar-fit"
            onClick={onExpandAll}
            title="Expand all collapsed branches"
          >
            ⊞ Expand all
          </button>
        ) : (
          <button
            type="button"
            className="canvas-toolbar-fit"
            onClick={onCollapseAll}
            title="Collapse all branches to top level"
          >
            ⊟ Collapse all
          </button>
        )}
        <input
          type="text"
          className="canvas-toolbar-search"
          placeholder="Search nodes..."
          value={searchQuery}
          onChange={(e) => onSearchChange?.(e.target.value)}
          title="Filter by label or reasoning"
        />
        {nodeCount > 0 && (
          <span className="canvas-toolbar-count">{nodeCount} nodes</span>
        )}
      </div>
    </Panel>
  );
}

export default function IdeaCanvas({
  nodes,
  edges,
  isGenerating,
  isScoring,
  progressText,
  onNodeClick,
  onNodeContextMenu,
  onCloseContextMenu,
  onNodeDoubleClick,
  drillStack,
  onExitDrill,
  onJumpToBreadcrumb,
  searchQuery,
  onSearchChange,
  onReactFlowReady,
  onCollapseAll,
  onExpandAll,
  hasCollapsed,
  isCinematic = false,
  selectedNode = null,
  allNodes = null,
}) {
  // Track internal node dimensions — React Flow v12 needs to measure nodes before edges render
  const [internalNodes, setInternalNodes] = useState(nodes);
  const prevNodesRef = useRef(nodes);

  // Sync external nodes into internal state (preserve RF-measured dimensions)
  useEffect(() => {
    if (nodes !== prevNodesRef.current) {
      prevNodesRef.current = nodes;
      setInternalNodes(nodes);
    }
  }, [nodes]);

  const onNodesChange = useCallback((changes) => {
    setInternalNodes(nds => applyNodeChanges(changes, nds));
  }, []);
  const onEdgesChange = useCallback(() => {}, []);

  const miniMapNodeColor = useCallback((node) => {
    const config = getNodeConfig(node.data?.type);
    return config.color;
  }, []);

  const handleNodeContextMenu = useCallback(
    (event, node) => {
      event.preventDefault();
      onNodeContextMenu(node.id, event.clientX, event.clientY);
    },
    [onNodeContextMenu]
  );

  const handleNodeClick = useCallback(
    (_, node) => {
      onNodeClick(node);
    },
    [onNodeClick]
  );

  const handleNodeDoubleClick = useCallback(
    (_, node) => {
      if (onNodeDoubleClick) onNodeDoubleClick(node.id);
    },
    [onNodeDoubleClick]
  );

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <NoveltyFilterBar />
      <ReactFlow
        nodes={internalNodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        fitView={!isCinematic}
        fitViewOptions={{ padding: 0.2, duration: 400 }}
        minZoom={0.05}
        maxZoom={2}
        proOptions={proOptions}
        style={{ background: '#0a0a0f' }}
        nodesDraggable={!isGenerating && !isCinematic}
        panOnDrag={!isCinematic}
        zoomOnScroll={!isCinematic}
        zoomOnPinch={!isCinematic}
        zoomOnDoubleClick={!isCinematic}
        elementsSelectable={!isCinematic}
        onNodeClick={handleNodeClick}
        onNodeDoubleClick={handleNodeDoubleClick}
        onNodeContextMenu={handleNodeContextMenu}
        onPaneClick={onCloseContextMenu}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={28}
          size={1}
          color="#1e1e2e"
        />
        <ReactFlowBridge onReady={onReactFlowReady} />
        {!isCinematic && <AutoFitView isGenerating={isGenerating} nodeCount={nodes.length} />}
        {!isCinematic && (
          <CanvasToolbar
            searchQuery={searchQuery ?? ''}
            onSearchChange={onSearchChange}
            nodeCount={nodes.length}
            onCollapseAll={onCollapseAll}
            onExpandAll={onExpandAll}
            hasCollapsed={hasCollapsed}
            drillStack={drillStack}
            onExitDrill={onExitDrill}
            onJumpToBreadcrumb={onJumpToBreadcrumb}
          />
        )}
        {!isCinematic && selectedNode && internalNodes.length > 0 && (
          <BreadcrumbRail
            selectedNode={selectedNode}
            nodes={internalNodes}
            onNodeClick={handleNodeClick}
          />
        )}
        {!isCinematic && (
          <Controls
            style={{
              background: '#16161f',
              border: '1px solid #2a2a3a',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
          />
        )}
        {!isCinematic && (
          <MiniMap
            nodeColor={miniMapNodeColor}
            maskColor="rgba(10,10,15,0.85)"
            style={{
              background: '#111118',
              border: '1px solid #2a2a3a',
              borderRadius: '8px',
            }}
          />
        )}
      </ReactFlow>

      {/* DrillBreadcrumb now integrated into CanvasToolbar panel above */}

      {isGenerating && (
        <div
          style={{
            position: 'absolute',
            bottom: '16px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#16161f',
            border: '1px solid #2a2a3a',
            borderRadius: '20px',
            padding: '8px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '11px',
            color: '#8888aa',
            letterSpacing: '0.06em',
            zIndex: 10,
          }}
        >
          <PulsingDot />
          {progressText || 'AGENT THINKING...'}
        </div>
      )}
      {isScoring && !isGenerating && (
        <div
          style={{
            position: 'absolute',
            bottom: '16px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#16161f',
            border: '1px solid #2a2a3a',
            borderRadius: '20px',
            padding: '8px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '11px',
            color: '#8888aa',
            letterSpacing: '0.06em',
            zIndex: 10,
          }}
        >
          <PulsingDot />
          SCORING NODES...
        </div>
      )}
    </div>
  );
}

function PulsingDot() {
  return (
    <span
      style={{
        display: 'inline-block',
        width: '7px',
        height: '7px',
        borderRadius: '50%',
        background: '#6c63ff',
        animation: 'pulse 1.2s ease-in-out infinite',
      }}
    />
  );
}
